import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, CheckCircle2, Shield, Users, Star, 
  Play, Calendar, MessageSquare, FileText, Sparkles,
  GraduationCap, Briefcase, MapPin, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CountryCard } from '../components/CountryCard';
import { SessionCard } from '../components/SessionCard';
import { VideoPlaceholder } from '../components/VideoEmbed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Study abroad focused images
const STUDENT_IMAGE_1 = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=800";
const STUDENT_IMAGE_2 = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=600";
const STUDENT_IMAGE_3 = "https://images.unsplash.com/photo-1627556704302-624286467c65?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.0.3&q=85&w=600";

export const HomePage = () => {
  const [countries, setCountries] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [countriesRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/countries`),
          axios.get(`${API}/sessions/upcoming?limit=6`)
        ]);
        setCountries(countriesRes.data);
        setSessions(sessionsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'No Spam, Ever',
      description: 'We respect your inbox. Only relevant, valuable information when you need it.'
    },
    {
      icon: CheckCircle2,
      title: 'Honest Advice',
      description: 'No forced selling. Just facts and personalized guidance based on your profile.'
    },
    {
      icon: Users,
      title: 'Expat Advisors',
      description: 'Learn from Indians who\'ve already made the move and built successful lives in Europe.'
    },
    {
      icon: Star,
      title: 'Profile-Based Guidance',
      description: 'Recommendations tailored to your skills, experience, and career aspirations.'
    }
  ];

  const testimonials = [
    {
      name: 'Rahul Sharma',
      role: 'Software Engineer, Berlin',
      content: 'TieClad helped me understand the real process of moving to Germany. No false promises, just practical advice that actually worked.',
      rating: 5
    },
    {
      name: 'Priya Patel',
      role: 'Student, Amsterdam',
      content: 'Unlike other consultants who just wanted to sell courses, TieClad genuinely guided me based on my profile. Now studying in Netherlands!',
      rating: 5
    },
    {
      name: 'Arjun Menon',
      role: 'Data Analyst, Paris',
      content: 'The live sessions were incredibly informative. Real questions answered by real expats. Highly recommend!',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'What makes TieClad different from other immigration consultants?',
      answer: 'We\'re expats ourselves who\'ve been through the journey. We provide honest, profile-based guidance without any pressure to buy services. Our advice is based on facts, not sales targets.'
    },
    {
      question: 'Are the live sessions really free?',
      answer: 'Yes! Most of our live sessions are completely free. We believe in education first. We also offer premium masterclasses for deeper dives into specific topics.'
    },
    {
      question: 'How do I know which European country is right for me?',
      answer: 'Book a free profile evaluation! We\'ll assess your qualifications, experience, language skills, and goals to recommend the best-fit countries and pathways.'
    },
    {
      question: 'Do you help with visa applications?',
      answer: 'We provide guidance and information about visa processes. We help you understand requirements and prepare, but the actual application must be submitted by you to official authorities.'
    },
    {
      question: 'What topics do your sessions cover?',
      answer: 'Study opportunities, scholarship programs, work visas, job market insights, Ausbildung (vocational training), settlement pathways, cost of living, and practical tips for life in Europe.'
    }
  ];

  const stats = [
    { number: '5,000+', label: 'Students Guided', icon: GraduationCap },
    { number: '10', label: 'European Countries', icon: Globe },
    { number: '50+', label: 'Live Sessions', icon: Play },
    { number: '4.9★', label: 'Average Rating', icon: Star },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#FFFBF8]">
      {/* Hero Section - Light Theme */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F0FAF6] via-[#FFFBF8] to-[#EEF4FF] opacity-80" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#0AA06A]/5 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-[#0AA06A]/10 text-[#0AA06A] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <GraduationCap size={18} />
                Trusted by 5,000+ Study Abroad Aspirants
              </div>

              {/* Main Heading with highlighted words */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#002A76] mb-6 leading-relaxed">
                Simplifying <span className="text-[#0AA06A] whitespace-nowrap">Europe Immigration</span>
                <br />
                for Your Next Career Move
              </h1>

              {/* Subheading */}
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-xl">
                Get <span className="font-semibold text-[#002A76]">honest advice</span> from Indians who've already made the move. 
                No spam, no forced selling — just{' '}
                <span className="font-semibold text-[#0AA06A]">genuine guidance</span> for your Europe journey.
              </p>

              {/* Value Props */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-gray-700 bg-white px-4 py-2 rounded-full shadow-sm">
                  <CheckCircle2 size={18} className="text-[#0AA06A]" />
                  <span className="text-sm font-medium">Free Scholarships Info</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 bg-white px-4 py-2 rounded-full shadow-sm">
                  <CheckCircle2 size={18} className="text-[#0AA06A]" />
                  <span className="text-sm font-medium">Visa Guidance</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700 bg-white px-4 py-2 rounded-full shadow-sm">
                  <CheckCircle2 size={18} className="text-[#0AA06A]" />
                  <span className="text-sm font-medium">Expat Mentors</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/profile-evaluation">
                  <Button 
                    data-testid="hero-cta-evaluation"
                    className="bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full px-8 py-6 text-lg font-semibold btn-hover-lift w-full sm:w-auto shadow-lg shadow-[#0AA06A]/20"
                  >
                    Get Free Profile Evaluation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/sessions">
                  <Button 
                    data-testid="hero-cta-sessions"
                    variant="outline"
                    className="border-2 border-[#002A76] text-[#002A76] hover:bg-[#002A76] hover:text-white rounded-full px-8 py-6 text-lg font-semibold w-full sm:w-auto"
                  >
                    Explore Free Sessions
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Visual Element */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Main visual card with gradient */}
                <div className="relative z-10 bg-gradient-to-br from-[#002A76] to-[#0AA06A] rounded-3xl p-8 shadow-2xl">
                  {/* Country flags grid */}
                  <div className="grid grid-cols-5 gap-3 mb-6">
                    {['🇩🇪', '🇳🇱', '🇫🇷', '🇮🇹', '🇪🇸', '🇵🇹', '🇵🇱', '🇧🇪', '🇸🇪', '🇫🇮'].map((flag, i) => (
                      <div key={i} className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-2xl hover:scale-110 transition-transform cursor-pointer">
                        {flag}
                      </div>
                    ))}
                  </div>
                  
                  {/* Stats inside */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-[#0AA06A] flex items-center justify-center">
                        <GraduationCap size={28} className="text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#002A76] text-lg">Study in Top EU Universities</p>
                        <p className="text-sm text-gray-600">Germany • Netherlands • France & 7 more</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#F7F4F0] rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-[#0AA06A]">€0</p>
                        <p className="text-xs text-gray-600">Tuition in Germany</p>
                      </div>
                      <div className="bg-[#F7F4F0] rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-[#002A76]">100+</p>
                        <p className="text-xs text-gray-600">Universities</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg z-20 animate-bounce">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#0AA06A] flex items-center justify-center">
                      <CheckCircle2 size={16} className="text-white" />
                    </div>
                    <span className="text-sm font-semibold text-[#002A76]">Free Advice</span>
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#002A76]/10 rounded-full" />
                <div className="absolute top-1/3 -right-8 w-16 h-16 bg-[#0AA06A]/20 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <stat.icon size={20} className="text-[#0AA06A]" />
                  <span className="text-2xl md:text-3xl font-bold text-[#002A76]">{stat.number}</span>
                </div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">How We Help</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
              Your Path to <span className="text-[#0AA06A]">Europe</span>, Simplified
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose your preferred way to learn and get guidance on your study abroad journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free Sessions */}
            <Link 
              to="/sessions?type=free_session"
              data-testid="offer-free-sessions"
              className="group bg-white rounded-2xl p-6 border border-gray-100 card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-[#0AA06A]/10 flex items-center justify-center mb-4 group-hover:bg-[#0AA06A] transition-colors">
                <Play size={24} className="text-[#0AA06A] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#002A76] mb-2">Free Live Sessions</h3>
              <p className="text-gray-600 text-sm mb-4">
                Interactive sessions on scholarships, admissions & visa process
              </p>
              <span className="flex items-center text-[#0AA06A] font-medium text-sm">
                Join Now <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            {/* Masterclasses */}
            <Link 
              to="/sessions?type=paid_masterclass"
              data-testid="offer-masterclass"
              className="group bg-white rounded-2xl p-6 border border-gray-100 card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center mb-4 group-hover:bg-[#F59E0B] transition-colors">
                <Sparkles size={24} className="text-[#F59E0B] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#002A76] mb-2">Premium Masterclass</h3>
              <p className="text-gray-600 text-sm mb-4">
                Deep-dive sessions with Q&A, resources & extended support
              </p>
              <span className="flex items-center text-[#F59E0B] font-medium text-sm">
                Explore <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            {/* 1:1 Call */}
            <Link 
              to="/book-call"
              data-testid="offer-call"
              className="group bg-white rounded-2xl p-6 border border-gray-100 card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-[#002A76]/10 flex items-center justify-center mb-4 group-hover:bg-[#002A76] transition-colors">
                <MessageSquare size={24} className="text-[#002A76] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#002A76] mb-2">Book 1:1 Call</h3>
              <p className="text-gray-600 text-sm mb-4">
                20-min personalized consultation with an expat advisor
              </p>
              <span className="flex items-center text-[#002A76] font-medium text-sm">
                Book Now <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            {/* Profile Evaluation */}
            <Link 
              to="/profile-evaluation"
              data-testid="offer-evaluation"
              className="group bg-white rounded-2xl p-6 border border-gray-100 card-hover"
            >
              <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-4 group-hover:bg-[#3B82F6] transition-colors">
                <FileText size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-[#002A76] mb-2">Profile Evaluation</h3>
              <p className="text-gray-600 text-sm mb-4">
                Get personalized university & country recommendations
              </p>
              <span className="flex items-center text-[#3B82F6] font-medium text-sm">
                Get Evaluated <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section id="countries" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">Explore Destinations</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
              <span className="text-[#0AA06A]">10</span> European Countries. Endless Opportunities.
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Click on a country to explore study programs, scholarships, and settlement options
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="bg-[#F7F4F0] rounded-2xl p-6 animate-pulse">
                  <div className="w-12 h-12 bg-gray-200 rounded-full mb-4" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {countries.map((country, index) => (
                <CountryCard 
                  key={country.id} 
                  country={country}
                  isLarge={false}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="py-16 md:py-24 bg-[#FFFBF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">Live & Interactive</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
                Upcoming <span className="text-[#0AA06A]">Sessions</span>
              </h2>
              <p className="text-gray-600 max-w-xl">
                Join our free live sessions and premium masterclasses led by expats settled in Europe
              </p>
            </div>
            <Link to="/sessions" className="mt-4 md:mt-0">
              <Button 
                data-testid="view-all-sessions"
                variant="outline"
                className="border-2 border-[#002A76] text-[#002A76] hover:bg-[#002A76] hover:text-white rounded-full px-6"
              >
                View All Sessions
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-5">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.slice(0, 6).map((session) => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No upcoming sessions. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Why TieClad */}
      <section className="py-16 md:py-24 bg-[#002A76]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">Why Choose Us</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">The TieClad Difference</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              We're not your typical immigration consultants. Here's what sets us apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0AA06A] flex items-center justify-center mb-4">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-300 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section Placeholder */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">Watch & Learn</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
              Success Stories & <span className="text-[#0AA06A]">Insights</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hear from Indians who've successfully made the move to Europe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <VideoPlaceholder type="youtube" title="How I Got Into German University" />
            <VideoPlaceholder type="youtube" title="Netherlands Scholarship Guide" />
            <VideoPlaceholder type="instagram" title="Day in Life of Indian Student in Europe" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-[#F7F4F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
              Trusted by <span className="text-[#0AA06A]">Thousands</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#F59E0B] fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold text-[#002A76]">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#0AA06A] font-semibold text-sm uppercase tracking-widest mb-3">FAQs</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#002A76] mb-4">
              Frequently Asked <span className="text-[#0AA06A]">Questions</span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#F7F4F0] rounded-xl border-none px-6"
              >
                <AccordionTrigger 
                  data-testid={`faq-trigger-${index}`}
                  className="text-left font-semibold text-[#002A76] hover:text-[#0AA06A] py-4"
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#002A76] to-[#001F5C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your <span className="text-[#0AA06A]">Study Abroad</span> Journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a free profile evaluation and discover the best European universities for you. No spam, no pressure – just honest advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/profile-evaluation">
              <Button 
                data-testid="final-cta-evaluation"
                className="bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full px-8 py-6 text-lg font-semibold btn-hover-lift"
              >
                Get Free Evaluation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/book-call">
              <Button 
                data-testid="final-cta-call"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#002A76] rounded-full px-8 py-6 text-lg font-semibold"
              >
                Book a Free Call
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
